import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Accordion, AccordionSummary, AccordionDetails, Stack, Chip, IconButton, Typography } from '@mui/material';
import { ExpandMore, DeleteOutline } from '@mui/icons-material';
import palette from '@theme/palette';
import { useTranslation } from 'react-i18next';
import { useDeleteSolution, useFile, useValidationUiStore } from '@entities/solution';
import { SystemSelector } from './SystemSelector/SystemSelector';
const GROUPS = [
    { title: 'Wall Form', key: 'wall' },
    { title: 'Slab', key: 'slab' },
    { title: 'Scaffolding', key: 'scaffold' }
];
export function ProjectTabs() {
    const { t } = useTranslation();
    const { id: fileId } = useParams();
    const { data: file } = useFile(fileId);
    const { deleteSolution } = useDeleteSolution(fileId || '');
    const { selectedSolutionId, expandedPanel, selectSolution, setExpandedPanel } = useValidationUiStore();
    const [hoveredSolutionId, setHoveredSolutionId] = React.useState('');
    const handleAccordionChange = (panel) => (_event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };
    const handleDelete = (e, solutionId) => {
        e.stopPropagation();
        // The file query is invalidated on success; HierarchyPage re-selects a solution
        // automatically if the deleted one was selected.
        deleteSolution(solutionId);
    };
    const renderGroup = (title, key) => {
        var _a;
        const solutions = ((_a = file === null || file === void 0 ? void 0 : file.solutions) === null || _a === void 0 ? void 0 : _a[key]) || [];
        const count = solutions.length;
        const isExpanded = expandedPanel === key;
        return (_jsx(Box, { children: _jsxs(Accordion, { expanded: isExpanded, onChange: handleAccordionChange(key), sx: {
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    backgroundColor: 'transparent',
                    '& .MuiAccordionSummary-root': {
                        minHeight: '56px',
                        padding: '0 24px',
                        '&:hover': { backgroundColor: palette.neutral[95] }
                    },
                    '& .MuiAccordionDetails-root': {
                        padding: 0,
                        backgroundColor: '#fafafa'
                    }
                }, children: [_jsx(AccordionSummary, { expandIcon: _jsx(ExpandMore, { sx: { color: palette.neutral[60] } }), sx: {
                            '& .MuiAccordionSummary-content': { margin: '16px 0', alignItems: 'center' }
                        }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 500, color: palette.neutral[20], fontSize: '16px' }, children: t(title) }), _jsx(Chip, { label: `${count} ${t('solution')}${count !== 1 ? 's' : ''}`, size: "small", sx: {
                                        height: '24px',
                                        fontSize: '12px',
                                        backgroundColor: count > 0 ? 'rgba(56, 142, 255, 0.12)' : palette.neutral[95],
                                        color: count > 0 ? palette.primary.main : palette.neutral[60],
                                        fontWeight: 500
                                    } })] }) }), count > 0 && (_jsx(AccordionDetails, { children: _jsx(Stack, { spacing: 0, children: solutions.map(solution => {
                                const isHovered = hoveredSolutionId === solution.solutionID;
                                const isSelected = selectedSolutionId === solution.solutionID;
                                return (_jsxs(Box, { onClick: () => selectSolution(solution.solutionID, key), onMouseEnter: () => setHoveredSolutionId(solution.solutionID), onMouseLeave: () => setHoveredSolutionId(''), sx: {
                                        padding: '12px 50px',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected
                                            ? 'rgba(33, 150, 243, 0.08)'
                                            : isHovered
                                                ? 'rgba(0, 0, 0, 0.02)'
                                                : 'white',
                                        transition: 'background-color 0.2s ease',
                                        borderLeft: isSelected
                                            ? `3px solid ${palette.primary.main}`
                                            : '3px solid transparent'
                                    }, children: [_jsxs(Box, { sx: {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }, children: [_jsx(Typography, { variant: "body2", sx: {
                                                        fontWeight: isSelected ? 600 : 500,
                                                        color: isSelected ? palette.primary.main : palette.neutral[30],
                                                        fontSize: '14px'
                                                    }, children: solution.name }), _jsx(IconButton, { size: "small", onClick: e => handleDelete(e, solution.solutionID), "aria-label": "delete solution", sx: {
                                                        padding: '6px',
                                                        color: '#D32F2F',
                                                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' }
                                                    }, children: _jsx(DeleteOutline, { sx: { fontSize: 20 } }) })] }), isSelected && fileId ? (_jsx(Box, { sx: { mt: 1 }, onClick: e => e.stopPropagation(), children: _jsx(SystemSelector, { systems: solution.systems, solutionId: solution.solutionID, fileId: fileId, category: key }) })) : (_jsx(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }, children: solution.systems.map(system => (_jsx(Chip, { label: system, size: "small", sx: {
                                                    height: '24px',
                                                    fontSize: '12px',
                                                    backgroundColor: '#F4F1FF',
                                                    color: palette.neutral[20],
                                                    fontWeight: 500
                                                } }, system))) }))] }, solution.solutionID));
                            }) }) }))] }) }, key));
    };
    return (_jsxs(Box, { sx: { width: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }, children: [_jsx(Box, { sx: { padding: '12px 12px 8px 10px' }, children: _jsx(Typography, { variant: "h2", sx: { fontWeight: 700, color: palette.neutral[20], fontSize: '24px' }, children: t('Solutions') }) }), _jsx(Stack, { spacing: 0, children: GROUPS.map(group => renderGroup(group.title, group.key)) })] }));
}
